import { AdditionalInfo } from '../models/classes/additionalinfo';

export class AdditionalInfoUtil {
  static getConvertedValue(info: AdditionalInfo): number | null {
    // a hack to stop rounding errors from preventing user input
    if (info.valueInProgess !== null) {
      return info.valueInProgess;
    }

    if (info.value === null || info.value === undefined) {
      return null;
    }

    if (info.valueType === 'temperature') {
      const tempValue =
        info.currentMeasurementSystem === 'im'
          ? info.value * (9 / 5) + 32 // celsius to fahrenheit
          : info.value;
      return (
        Math.round((tempValue + Number.EPSILON) * 100) / 100 // always round to two decimal places
      );
    }

    const system = info.currentMeasurementSystem as 'mt' | 'im';
    const factor =
      typeof info.conversionFactor === 'object' && info.conversionFactor?.[system]
        ? info.conversionFactor[system]
        : 1;

    return info.value / factor;
  }

  static setConvertedValue(info: AdditionalInfo, convertedValue: number): AdditionalInfo {
    const updated = { ...info };
    updated.valueInProgess = convertedValue;

    if (info.valueType === 'temperature') {
      updated.value =
        info.currentMeasurementSystem === 'im' ? (convertedValue - 32) * (5 / 9) : convertedValue;
    } else {
      const system = info.currentMeasurementSystem as 'mt' | 'im';
      const factor =
        typeof info.conversionFactor === 'object' && info.conversionFactor?.[system]
          ? info.conversionFactor[system]
          : 1;
      updated.value = convertedValue * factor;
    }

    return updated as AdditionalInfo;
  }

  static getLabelKey(
    info: AdditionalInfo,
    measurementSystem: string,
    parseFunction: (text: string) => string | Record<string, string>
  ): string {
    const parsed = parseFunction(info.category);
    if (typeof parsed === 'object' && measurementSystem in parsed) {
      return parsed[measurementSystem];
    }
    return typeof parsed === 'string' ? parsed : info.category;
  }

  /**
   * Get parsed validation message
   * @param info - AdditionalInfo
   * @param message - Message to parse
   * @param measurementSystem - Current measurement system
   * @param parseFunction - Function to parse i18n strings
   * @returns Parsed validation message for current measurement system
   */
  static getParsedValidationMessage(
    info: AdditionalInfo,
    message: string,
    measurementSystem: string,
    parseFunction: (text: string) => string | Record<string, string>
  ): string {
    const parsed = parseFunction(message);
    if (typeof parsed === 'object' && measurementSystem in parsed) {
      return parsed[measurementSystem];
    }
    return typeof parsed === 'string' ? parsed : message;
  }

  static getParsedPlaceholder(
    info: AdditionalInfo,
    measurementSystem: string,
    parseFunction: (text: string) => string | Record<string, string>
  ): string {
    const text = info.placeholder || info.category;
    const parsed = parseFunction(text);

    if (typeof parsed === 'object' && measurementSystem in parsed) {
      return parsed[measurementSystem];
    }
    return typeof parsed === 'string' ? parsed : text;
  }

  static createFromInstance(obj: Partial<AdditionalInfo>): AdditionalInfo {
    return {
      objectID: obj.objectID ?? 0,
      categoryID: obj.categoryID ?? 0,
      category: obj.category ?? '',
      disabled: obj.disabled ?? false,
      optional: obj.optional ?? false,
      fieldType: obj.fieldType ?? '',
      placeholder: obj.placeholder ?? '',
      valueInProgess: obj.valueInProgess ?? null,
      infoMessage: obj.infoMessage ?? [],
      currentMeasurementSystem: obj.currentMeasurementSystem ?? 'mt',
      showValidation: obj.showValidation ?? false,
      validations: obj.validations ?? [],
      ...obj,
    } as AdditionalInfo;
  }

  static convertTemperature(value: number, fromSystem: string): number {
    if (fromSystem === 'im') {
      // Fahrenheit to Celsius
      return (value - 32) * (5 / 9);
    } else {
      // Celsius to Fahrenheit
      return value * (9 / 5) + 32;
    }
  }

  static roundToDecimals(value: number, decimals = 2): number {
    return Math.round((value + Number.EPSILON) * Math.pow(10, decimals)) / Math.pow(10, decimals);
  }

  static isValueValid(info: AdditionalInfo, value: number): boolean {
    if (info.minValue !== undefined && value < info.minValue) {
      return false;
    }
    if (info.maxValue !== undefined && value > info.maxValue) {
      return false;
    }
    return true;
  }

  static isDropdownField(info: AdditionalInfo): boolean {
    return info.fieldType === 'dropdown' && !!info.dropdownValues?.length;
  }

  static isMultipleField(info: AdditionalInfo): boolean {
    return info.fieldType === 'multiple';
  }

  static requiresConversion(info: AdditionalInfo): boolean {
    return !!(info.conversionFactor || info.valueType === 'temperature');
  }
}
